const CART_PAGE_RECOMMENDATION_REQUEST_COOLDOWN_MS = 8000;
const CART_PAGE_RECOMMENDATION_RATE_LIMIT_BACKOFF_MS = 60000;
const CART_PAGE_RECOMMENDATIONS_SESSION_STORAGE_KEY = 'omtechDeCartPageRecommendations';
const CART_PAGE_RECOMMENDATIONS_ROOT_ID = 'CartPageRecommendations';
const CART_PAGE_RECOMMENDATIONS_MOBILE_MAX_WIDTH = 768;

function readCartPageRecommendationsSessionState() {
  try {
    const storedValue = window.sessionStorage.getItem(CART_PAGE_RECOMMENDATIONS_SESSION_STORAGE_KEY);
    if (!storedValue) return null;

    return JSON.parse(storedValue);
  } catch (error) {
    return null;
  }
}

function persistCartPageRecommendationsStore(store) {
  try {
    const serializedState = JSON.stringify({
      cache: Object.fromEntries(store.cache),
      lastRequestedAt: Object.fromEntries(store.lastRequestedAt),
      rateLimitedUntil: Object.fromEntries(store.rateLimitedUntil),
    });

    window.sessionStorage.setItem(CART_PAGE_RECOMMENDATIONS_SESSION_STORAGE_KEY, serializedState);
  } catch (error) {
    // Ignore storage failures so recommendations still work in-memory.
  }
}

function getCartPageRecommendationsStore() {
  if (!window.__cartPageRecommendationsStore) {
    const sessionState = readCartPageRecommendationsSessionState();

    window.__cartPageRecommendationsStore = {
      cache: new Map(Object.entries(sessionState?.cache || {})),
      inFlightRequests: new Map(),
      lastRequestedAt: new Map(
        Object.entries(sessionState?.lastRequestedAt || {}).map(([key, value]) => [key, Number(value) || 0])
      ),
      rateLimitedUntil: new Map(
        Object.entries(sessionState?.rateLimitedUntil || {}).map(([key, value]) => [key, Number(value) || 0])
      ),
    };
  }

  return window.__cartPageRecommendationsStore;
}

function setLatestCartPageRecommendationSeed(productId) {
  if (!productId) return;
  window.__cartPageLatestRecommendationProductId = String(productId);
}

function getLatestCartPageRecommendationSeed() {
  return window.__cartPageLatestRecommendationProductId || null;
}

function getCartPageSectionsToRender() {
  const cartItemsElement = document.querySelector('cart-items');
  if (!cartItemsElement || typeof cartItemsElement.getSectionsToRender !== 'function') return [];

  return cartItemsElement.getSectionsToRender().filter((section) => section.id !== CART_PAGE_RECOMMENDATIONS_ROOT_ID);
}

function getSectionInnerHTML(html, selector) {
  const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
  const target = parsedDocument.querySelector(selector);
  return target ? target.innerHTML : '';
}

function updateCartPageFromAddResponse(parsedState) {
  const cartItemsElement = document.querySelector('cart-items');
  if (!cartItemsElement || !parsedState?.sections) return;

  cartItemsElement.classList.toggle('is-empty', parsedState.item_count === 0);

  const cartFooter = document.getElementById('main-cart-footer');
  if (cartFooter) {
    cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
  }

  getCartPageSectionsToRender().forEach((section) => {
    const sectionContainer = document.getElementById(section.id);
    if (!sectionContainer || !parsedState.sections[section.section]) return;

    const replacementTarget = sectionContainer.querySelector(section.selector) || sectionContainer;
    replacementTarget.innerHTML = getSectionInnerHTML(parsedState.sections[section.section], section.selector);
  });
}

class CartPageRecommendations extends HTMLElement {
  connectedCallback() {
    this.handleDocumentClick = this.handleDocumentClick?.bind(this) || this.onDocumentClick.bind(this);
    this.handleResize = this.handleResize?.bind(this) || this.onResize.bind(this);
    this.fallbackMarkup =
      this.fallbackMarkup ||
      this.querySelector('[data-fallback-recommendations]')?.innerHTML.trim() ||
      '';

    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('resize', this.handleResize);

    this.defaultProductId = getLatestCartPageRecommendationSeed() || this.dataset.defaultProductId || '';

    if (this.defaultProductId) {
      this.loadRecommendationsForProduct(this.defaultProductId);
      return;
    }

    this.showFallbackRecommendations();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('resize', this.handleResize);
    this.destroySwiper();
  }

  buildRecommendationUrl(productId) {
    const baseUrl = this.dataset.baseUrl;
    if (!baseUrl || !productId) return null;

    const requestUrl = new URL(baseUrl, window.location.origin);
    requestUrl.searchParams.set('section_id', this.dataset.sectionId || '');
    requestUrl.searchParams.set('product_id', productId);
    requestUrl.searchParams.set('limit', this.dataset.limit || '20');
    return requestUrl.toString();
  }

  getCachedRecommendationMarkup(recommendationKey) {
    if (!recommendationKey) return null;

    const cacheEntry = getCartPageRecommendationsStore().cache.get(recommendationKey);
    return cacheEntry?.markup || null;
  }

  renderRecommendationMarkup(markup, recommendationKey) {
    if (!markup) {
      this.showFallbackRecommendations();
      return;
    }

    this.innerHTML = markup;
    this.hidden = false;
    this.currentProductId = recommendationKey || this.currentProductId;
    this.dataset.loaded = 'true';
    this.initializeContent();
  }

  async fetchRecommendationsMarkup(url) {
    const response = await fetch(url);

    if (!response.ok) {
      const error = new Error(`Failed to load recommendations: ${response.status}`);
      error.status = response.status;
      throw error;
    }

    const text = await response.text();
    const html = document.createElement('div');
    html.innerHTML = text;

    const recommendationSection = html.querySelector('[data-cart-page-recommendations-section]');
    if (!recommendationSection) return null;

    const markup = recommendationSection.innerHTML.trim();
    return markup.length ? markup : null;
  }

  async loadRecommendationsForProduct(productId, options = {}) {
    const recommendationKey = String(productId || '');
    if (!recommendationKey || this.isLoadingRecommendations) return;

    if (!options.force && this.currentProductId === recommendationKey && this.dataset.loaded === 'true') return;

    const url = this.buildRecommendationUrl(recommendationKey);
    if (!url) {
      this.showFallbackRecommendations();
      return;
    }

    const recommendationsStore = getCartPageRecommendationsStore();
    const now = Date.now();
    const cachedMarkup = this.getCachedRecommendationMarkup(recommendationKey);

    this.currentProductId = recommendationKey;
    this.defaultProductId = recommendationKey;
    setLatestCartPageRecommendationSeed(recommendationKey);

    if (cachedMarkup) {
      this.renderRecommendationMarkup(cachedMarkup, recommendationKey);
      return;
    }

    const rateLimitedUntil = recommendationsStore.rateLimitedUntil.get(recommendationKey) || 0;
    if (rateLimitedUntil > now) {
      this.showFallbackRecommendations();
      return;
    }

    const inFlightRequest = recommendationsStore.inFlightRequests.get(recommendationKey);
    if (inFlightRequest) {
      this.isLoadingRecommendations = true;

      try {
        const markup = await inFlightRequest;
        if (markup) {
          this.renderRecommendationMarkup(markup, recommendationKey);
        } else {
          this.showFallbackRecommendations();
        }
      } catch (error) {
        const fallbackMarkup = this.getCachedRecommendationMarkup(recommendationKey);
        if (fallbackMarkup) {
          this.renderRecommendationMarkup(fallbackMarkup, recommendationKey);
        } else {
          this.showFallbackRecommendations();
        }
      } finally {
        this.isLoadingRecommendations = false;
      }

      return;
    }

    const lastRequestedAt = recommendationsStore.lastRequestedAt.get(recommendationKey) || 0;
    if (!options.force && now - lastRequestedAt < CART_PAGE_RECOMMENDATION_REQUEST_COOLDOWN_MS) {
      this.showFallbackRecommendations();
      return;
    }

    this.isLoadingRecommendations = true;
    const requestPromise = this.fetchRecommendationsMarkup(url);
    recommendationsStore.lastRequestedAt.set(recommendationKey, now);
    recommendationsStore.inFlightRequests.set(recommendationKey, requestPromise);
    persistCartPageRecommendationsStore(recommendationsStore);

    try {
      const markup = await requestPromise;

      if (markup) {
        recommendationsStore.cache.set(recommendationKey, {
          markup,
          cachedAt: Date.now(),
        });
        persistCartPageRecommendationsStore(recommendationsStore);
        this.renderRecommendationMarkup(markup, recommendationKey);
        return;
      }

      this.showFallbackRecommendations();
    } catch (error) {
      if (error.status === 429) {
        recommendationsStore.rateLimitedUntil.set(
          recommendationKey,
          Date.now() + CART_PAGE_RECOMMENDATION_RATE_LIMIT_BACKOFF_MS
        );
        persistCartPageRecommendationsStore(recommendationsStore);
      }

      const fallbackMarkup = this.getCachedRecommendationMarkup(recommendationKey);
      if (fallbackMarkup) {
        this.renderRecommendationMarkup(fallbackMarkup, recommendationKey);
      } else {
        this.showFallbackRecommendations();
      }
    } finally {
      recommendationsStore.inFlightRequests.delete(recommendationKey);
      this.isLoadingRecommendations = false;
    }
  }

  showFallbackRecommendations() {
    const fallbackMarkup =
      this.fallbackMarkup ||
      this.querySelector('[data-fallback-recommendations]')?.innerHTML.trim() ||
      '';

    if (!fallbackMarkup) {
      this.hidden = true;
      this.innerHTML = '';
      return;
    }

    this.hidden = false;
    this.innerHTML = fallbackMarkup;
    this.dataset.loaded = 'true';
    this.initializeContent();
  }

  initializeContent() {
    this.initializeCarousel();
    this.initializeCards();
  }

  initializeCarousel() {
    const section = this.querySelector('.cart-page-recommendations');
    if (!section) return;

    const carousel = section.querySelector('[data-carousel]');
    const navigation = section.querySelector('[data-carousel-nav]');
    const slides = Array.from(section.querySelectorAll('[data-recommendation-card]'));
    const shouldEnableCarousel =
      window.innerWidth > CART_PAGE_RECOMMENDATIONS_MOBILE_MAX_WIDTH &&
      slides.length > 4 &&
      typeof window.Swiper !== 'undefined';

    section.classList.toggle('is-static', !shouldEnableCarousel);
    if (navigation) navigation.classList.toggle('is-hidden', !shouldEnableCarousel);

    this.destroySwiper();

    if (!shouldEnableCarousel || !carousel) return;

    this.swiper = new Swiper(carousel, {
      loop: false,
      slidesPerView: 4,
      slidesPerGroup: 1,
      spaceBetween: window.innerWidth >= 1025 ? 30 : 18.75,
      slidesOffsetBefore: 1,
      slidesOffsetAfter: 1,
      roundLengths: true,
      navigation: {
        nextEl: section.querySelector('[data-carousel-next]'),
        prevEl: section.querySelector('[data-carousel-prev]'),
      },
      watchOverflow: true,
    });
  }

  destroySwiper() {
    if (!this.swiper) return;

    this.swiper.destroy(true, true);
    this.swiper = null;
  }

  initializeCards() {
    this.cards = Array.from(this.querySelectorAll('[data-recommendation-card]'));
    this.cards.forEach((card) => this.initializeCard(card));
  }

  initializeCard(card) {
    if (card.dataset.initialized === 'true') return;

    const variantsNode = card.querySelector('[data-product-variants]');
    if (!variantsNode) return;

    let variants = [];

    try {
      variants = JSON.parse(variantsNode.textContent);
    } catch (error) {
      return;
    }

    if (!variants.length) return;

    const dropdown = card.querySelector('[data-recommendation-dropdown]');
    const variantTrigger = card.querySelector('[data-recommendation-variant-trigger]');
    const variantLabel = card.querySelector('[data-recommendation-variant-label]');
    const variantMenu = card.querySelector('[data-recommendation-variant-menu]');
    const variantOptions = Array.from(card.querySelectorAll('[data-recommendation-variant-option]'));
    const variantInput = card.querySelector('[data-recommendation-variant-input]');
    const submitButton = card.querySelector('[name="add"]');
    const submitTextDesktop = card.querySelector('[data-add-to-cart-text-desktop]');
    const submitTextMobile = card.querySelector('[data-add-to-cart-text-mobile]');
    const currentPrice = card.querySelector('[data-price-current]');
    const comparePrice = card.querySelector('[data-price-compare]');
    const savePrice = card.querySelector('[data-price-save]');
    const addForm = card.querySelector('[data-recommendation-add-form]');
    const spinner = card.querySelector('[data-recommendation-spinner]');
    const recommendationsSection = card.closest('.cart-page-recommendations');
    const availableLabelDesktop =
      submitButton?.dataset.availableLabelDesktop || submitTextDesktop?.textContent?.trim() || 'Add to cart';
    const availableLabelMobile =
      submitButton?.dataset.availableLabelMobile || submitTextMobile?.textContent?.trim() || availableLabelDesktop;

    const syncOpenDropdownState = () => {
      if (!recommendationsSection) return;

      recommendationsSection.classList.toggle(
        'has-open-dropdown',
        Boolean(this.querySelector('[data-recommendation-dropdown].is-open'))
      );
    };

    const setSubmitText = (desktopLabel, mobileLabel = desktopLabel) => {
      if (submitTextDesktop) submitTextDesktop.textContent = desktopLabel;
      if (submitTextMobile) submitTextMobile.textContent = mobileLabel;
    };

    const updateCard = (variantId) => {
      const nextVariant = variants.find((variant) => String(variant.id) === String(variantId));

      if (!nextVariant) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-disabled', 'true');
        setSubmitText(window.variantStrings?.unavailable || 'Unavailable');
        return;
      }

      variantInput.value = nextVariant.id;
      currentPrice.textContent = nextVariant.price_text;
      if (variantLabel) variantLabel.textContent = nextVariant.title || '';

      if (nextVariant.compare_at_price_text) {
        currentPrice.classList.add('is-sale');
        comparePrice.textContent = nextVariant.compare_at_price_text;
        comparePrice.hidden = false;
        if (nextVariant.save_text) {
          savePrice.textContent = nextVariant.save_text;
          savePrice.hidden = false;
        } else {
          savePrice.hidden = true;
          savePrice.textContent = '';
        }
      } else {
        currentPrice.classList.remove('is-sale');
        comparePrice.hidden = true;
        comparePrice.textContent = '';
        savePrice.hidden = true;
        savePrice.textContent = '';
      }

      if (nextVariant.available) {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-disabled');
        setSubmitText(availableLabelDesktop, availableLabelMobile);
      } else {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-disabled', 'true');
        setSubmitText(window.variantStrings?.soldOut || 'Sold out');
      }

      variantOptions.forEach((option) => {
        option.classList.toggle('is-selected', String(option.dataset.variantId) === String(nextVariant.id));
      });
    };

    if (dropdown && variantTrigger && variantMenu && variantOptions.length) {
      variantTrigger.addEventListener('click', () => {
        const isOpen = dropdown.classList.toggle('is-open');
        variantTrigger.setAttribute('aria-expanded', String(isOpen));
        variantMenu.hidden = !isOpen;
        syncOpenDropdownState();
      });

      variantOptions.forEach((option) => {
        option.addEventListener('click', () => {
          updateCard(option.dataset.variantId);
          dropdown.classList.remove('is-open');
          variantTrigger.setAttribute('aria-expanded', 'false');
          variantMenu.hidden = true;
          syncOpenDropdownState();
        });
      });
    }

    if (addForm && submitButton && variantInput) {
      addForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (submitButton.disabled || submitButton.dataset.loading === 'true') return;

        submitButton.dataset.loading = 'true';
        submitButton.disabled = true;
        submitButton.classList.add('is-loading');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(addForm);
        const sectionsToRender = getCartPageSectionsToRender();

        if (sectionsToRender.length) {
          formData.append('sections', sectionsToRender.map((section) => section.section));
          formData.append('sections_url', window.location.pathname);
        }

        config.body = formData;

        try {
          const response = await fetch(`${routes.cart_add_url}`, config);
          const result = await response.json();

          if (result.status) {
            setSubmitText(result.description || 'Unavailable');
            return;
          }

          updateCartPageFromAddResponse(result);

          const addedItems = Array.isArray(result.items) ? result.items : [];
          const latestAddedItem = addedItems[0] || result;
          const nextProductId =
            latestAddedItem?.product_id ||
            latestAddedItem?.product?.id ||
            latestAddedItem?.productId ||
            addForm.dataset.productId;

          if (nextProductId) {
            setLatestCartPageRecommendationSeed(nextProductId);
            await this.loadRecommendationsForProduct(nextProductId, { force: true });
          }

          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: {
              cart: result
            }
          }));
        } catch (error) {
          setSubmitText('Unavailable');
        } finally {
          delete submitButton.dataset.loading;
          submitButton.classList.remove('is-loading');
          if (spinner) spinner.classList.add('hidden');

          const currentVariant = variants.find((variant) => String(variant.id) === String(variantInput.value));
          submitButton.disabled = !currentVariant?.available;
          if (submitButton.disabled) {
            submitButton.setAttribute('aria-disabled', 'true');
            setSubmitText(window.variantStrings?.soldOut || 'Sold out');
          } else {
            submitButton.removeAttribute('aria-disabled');
            setSubmitText(availableLabelDesktop, availableLabelMobile);
          }
        }
      });
    }

    updateCard(variantInput.value || variants[0].id);
    card.dataset.initialized = 'true';
  }

  onDocumentClick(event) {
    const openDropdowns = this.querySelectorAll('[data-recommendation-dropdown].is-open');
    openDropdowns.forEach((dropdown) => {
      if (dropdown.contains(event.target)) return;
      dropdown.classList.remove('is-open');
      const trigger = dropdown.querySelector('[data-recommendation-variant-trigger]');
      const menu = dropdown.querySelector('[data-recommendation-variant-menu]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (menu) menu.hidden = true;
    });

    const recommendationsSection = this.querySelector('.cart-page-recommendations');
    if (recommendationsSection) {
      recommendationsSection.classList.remove('has-open-dropdown');
    }
  }

  onResize() {
    if (!this.dataset.loaded || this.hidden) return;
    this.initializeCarousel();
  }
}

if (!customElements.get('cart-page-recommendations')) {
  customElements.define('cart-page-recommendations', CartPageRecommendations);
}
